(ns hai.journal2.collection
  "Integration collection modals — integrations-modal."
  (:require
   [hai.sharing.components :as sharing.components]
   [hai.journal2.events :as events]
   [hai.journal2.integration-card :as integration-card]
   [hai.journal2.integration-registry :as reg]
   [hai.journal2.timeline :as timeline]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Icons
;; ---------------------------------------------------------------------------

(def ^:private import-icon (icons/mui-icon :outlined :file_download))

;; ---------------------------------------------------------------------------
;; Integration types
;; ---------------------------------------------------------------------------

(defn add-integration-dropdown []
  [c/DropdownMenuRoot
   [c/DropdownMenuTrigger
    [c/Button {:variant "soft" :size "2" :radius "full"
               :style {:background-color "var(--accent-a3)"}}
     "+ Add" [c/DropdownMenuTriggerIcon]]]
   [c/DropdownMenuContent
    (doall
     (for [t reg/connectable-types]
       ^{:key t}
       [c/DropdownMenuItem
        {:onSelect (fn [e]
                     (.preventDefault e)
                     (>e [:timeline/add-integration-by-type t]))}
        [c/Text {:size "2"} (reg/integration-type-label t)]]))]])

;; ---------------------------------------------------------------------------
;; Integrations table & modal
;; ---------------------------------------------------------------------------

(defn integrations-table []
  [collection/component
   {:id ::integrations-table
    :collection.view/default :collection.view/cards
    :query [{:timeline-integrations/table
             [{:collection/items
               [:timeline-integration/uuid
                {:timeline-integration/type [:db/ident]}
                :timeline-integration/connected?
                :timeline-integration/backfill-in-progress?
                :timeline-integration/connection-status
                :timeline-integration/connection-uuid
                :timeline-integration/connection-type
                :timeline-integration/connection-identity
                :user.entity/permissions
                sharing.components/shared-with-query]}]}]
    :paginate? false
    :card integration-card/render
    :bulk-actions [{:title "Remove"
                    :mutation-fn (fn [selected]
                                   (mapv (fn [item]
                                           (list 'integration/delete
                                                 {:timeline-integration/uuid
                                                  (:timeline-integration/uuid item)}))
                                         selected))
                    :disabled? (fn [item]
                                 (->> (get-in item [:timeline-integration/type :db/ident])
                                      (contains? reg/non-removable-types)))
                    :alert {:content (fn [{:table/keys [selected] :as table} action]
                                       [bulk-actions/confirm-dialog
                                        {:table table :action action}
                                        [bulk-actions/title "Remove Integrations"]
                                        [bulk-actions/description
                                         "Are you sure you want to remove the following integrations? Their entries will be deleted."]
                                        (into [:ul]
                                              (map (fn [item]
                                                     (let [type-ident (get-in item [:timeline-integration/type :db/ident])]
                                                       [:li [:strong (events/integration-type-label type-ident)]])))
                                              selected)])}}]
    :columns [{:header "Integration"
               :attr :timeline-integration/type
               :render (fn [item]
                         (let [type-ident (get-in item [:timeline-integration/type :db/ident])]
                           [table/table-cell
                            [c/Flex {:align "center" :gap "2"}
                             [timeline/source-icon
                              (reg/table-icon type-ident)]
                             [c/Text {:size "2" :weight "medium"}
                              (events/integration-type-label type-ident)]]]))}
              {:header "Status"
               :attr :timeline-integration/status
               :render (fn [item]
                         (let [type-ident       (get-in item [:timeline-integration/type :db/ident])
                               connected?       (:timeline-integration/connected? item)
                               backfilling?     (:timeline-integration/backfill-in-progress? item)
                               connectable?      (:connectable? (get reg/registry type-ident))
                               always-available? (not connectable?)]
                           [table/table-cell
                            [c/Flex {:align "center" :gap "2"}
                             (when backfilling?
                               [c/Spinner {:size "1"}])
                             (let [badge [c/Badge {:color (cond always-available? "blue"
                                                                backfilling?      "blue"
                                                                connected?        "green"
                                                                :else             "amber")
                                                   :variant "soft" :radius "full"}
                                          (cond always-available? "Always available"
                                                backfilling?      "Importing data now"
                                                connected?        "Connected"
                                                :else             "Disconnected")]]
                               (if (and (not always-available?) (not connected?) (not backfilling?))
                                 [c/Tooltip {:content "The connection has been deleted for this integration - no new events will be loaded. All existing events will remain."}
                                  badge]
                                 badge))]]))}
              sharing.components/shared-with-column
              {:header ""
               :attr :timeline-integration/actions
               :render (fn [item]
                         (let [type-ident   (get-in item [:timeline-integration/type :db/ident])
                               connectable? (:connectable? (get reg/registry type-ident))
                               connected?   (:timeline-integration/connected? item)]
                           [table/table-cell
                            (when (and connectable? connected?)
                              [c/Button {:variant "ghost" :size "1"
                                         :on-click #(>e [:timeline/open-backfill type-ident])}
                               [import-icon {:style {:width "16px" :height "16px"}}]
                               "Import history"])]))}]}])

(defn integrations-modal []
  (r/with-let [!open? (<s :timeline/integrations-modal-open?)]
    [c/DialogRoot
     {:open @!open?
      :onOpenChange (fn [open?]
                      (when-not open?
                        (>e [:timeline/close-integrations-modal])))}
     [c/DialogContent {:max-width "920px"}
      [c/DialogTitle "Manage Integrations"]
      [c/dialog-close-x]
      [c/DialogDescription "Add, remove, or share your integrations."]
      [c/Flex {:justify "end" :mb "3" :mt "3"}
       [add-integration-dropdown]]
      [integrations-table]
      [timeline/not-connected-modal]
      [timeline/backfill-modal]
      [c/Flex {:justify "end" :gap "3" :mt "4"}
       [c/DialogClose
        [c/Button {:variant "soft"} "Done"]]]]]))
